import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Text,
  Icon,
  Input,
  FormControl,
  FormLabel,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdFolder, MdChevronRight, MdCreateNewFolder } from "react-icons/md";
import { useCallback, useEffect, useState } from "react";
import { CustomAlert } from "components/alerts/CustomAlert";
import {
  apiListDriveFolders,
  type DriveFolder,
} from "api/integrations/driveFolders";

/** Nombre de la subcarpeta fija donde termina el PDF (informativo en la UI). */
export const PO_FACTORY_SUBFOLDER_NAME = "1. PO a Fábrica";

type Crumb = {
  /** id de carpeta; `null` representa la raíz configurada en el backend. */
  id: string | null;
  name: string;
};

export type DriveFolderPickerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Se invoca al confirmar: carpeta destino seleccionada + nombre a crear. */
  onConfirm: (folderId: string, folderName: string) => Promise<void> | void;
  isSaving?: boolean;
  /** Número de OC, solo para el encabezado. */
  orderNumber?: string | number;
};

const ROOT_CRUMB: Crumb = { id: null, name: "Compras" };

export function DriveFolderPickerModal(
  props: DriveFolderPickerModalProps,
): JSX.Element {
  const { isOpen, onClose, onConfirm, isSaving = false, orderNumber } = props;

  const [path, setPath] = useState<Crumb[]>([ROOT_CRUMB]);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = path[path.length - 1];
  const rowHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const rowSelectedBg = useColorModeValue("blue.50", "blue.900");

  const loadFolders = useCallback(async (parentId: string | null) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiListDriveFolders(parentId ?? undefined);
      setFolders(res.folders);
    } catch (err) {
      setFolders([]);
      setError(
        "No se pudieron cargar las carpetas de Drive. Verifica la conexión o la configuración.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Al abrir, reiniciar el picker y cargar la raíz.
  useEffect(() => {
    if (!isOpen) return;
    setPath([ROOT_CRUMB]);
    setSelectedId(null);
    setNewFolderName("");
    loadFolders(null);
  }, [isOpen, loadFolders]);

  const enterFolder = (folder: DriveFolder) => {
    setPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setSelectedId(null);
    loadFolders(folder.id);
  };

  const goToCrumb = (index: number) => {
    const target = path[index];
    setPath(path.slice(0, index + 1));
    setSelectedId(null);
    loadFolders(target.id);
  };

  // La carpeta destino es la seleccionada en la lista, o la carpeta actual si
  // se está dentro de una subcarpeta (no la raíz) sin selección explícita.
  const destinationId =
    selectedId ?? (current.id !== null ? current.id : null);
  const destinationName =
    selectedId != null
      ? folders.find((f) => f.id === selectedId)?.name ?? ""
      : current.id !== null
        ? current.name
        : "";

  const trimmedName = newFolderName.trim();
  const canConfirm = Boolean(destinationId) && trimmedName.length > 0 && !isSaving;

  const handleConfirm = async () => {
    if (!destinationId || trimmedName.length === 0) return;
    await onConfirm(destinationId, trimmedName);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Guardar auditoría en Drive
          {orderNumber != null ? (
            <Text fontSize="sm" fontWeight="normal" color="gray.500" mt={1}>
              {`Orden #${orderNumber}`}
            </Text>
          ) : null}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error ? (
            <CustomAlert
              message={error}
              status="error"
              mb="15px"
              borderRadius="12px"
            />
          ) : null}

          <Breadcrumb
            spacing="6px"
            separator={<Icon as={MdChevronRight} color="gray.400" />}
            mb="10px"
            fontSize="sm"
          >
            {path.map((crumb, index) => (
              <BreadcrumbItem
                key={`${crumb.id ?? "root"}-${index}`}
                isCurrentPage={index === path.length - 1}
              >
                <BreadcrumbLink onClick={() => goToCrumb(index)}>
                  {crumb.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
            ))}
          </Breadcrumb>

          <Text fontSize="xs" color="gray.500" mb="8px">
            Elige la subcarpeta donde se creará la nueva carpeta. Doble clic para
            entrar; un clic para seleccionarla como destino.
          </Text>

          <Box
            borderWidth="1px"
            borderRadius="12px"
            maxH="240px"
            overflowY="auto"
            mb="15px"
          >
            {isLoading ? (
              <Flex justify="center" align="center" py="30px">
                <Spinner size="sm" mr="10px" />
                <Text fontSize="sm">Cargando carpetas…</Text>
              </Flex>
            ) : folders.length === 0 ? (
              <Text fontSize="sm" color="gray.500" textAlign="center" py="30px">
                Esta carpeta no tiene subcarpetas.
              </Text>
            ) : (
              folders.map((folder) => (
                <Flex
                  key={folder.id}
                  align="center"
                  px="12px"
                  py="8px"
                  cursor="pointer"
                  bg={selectedId === folder.id ? rowSelectedBg : "transparent"}
                  _hover={{ bg: selectedId === folder.id ? rowSelectedBg : rowHoverBg }}
                  onClick={() => setSelectedId(folder.id)}
                  onDoubleClick={() => enterFolder(folder)}
                >
                  <Icon as={MdFolder} color="yellow.500" mr="10px" />
                  <Text fontSize="sm" noOfLines={1}>
                    {folder.name}
                  </Text>
                </Flex>
              ))
            )}
          </Box>

          <FormControl mb="10px">
            <FormLabel fontSize="sm" mb="4px">
              Nombre de la carpeta a crear
            </FormLabel>
            <Input
              variant="main"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Ej. OC-12345 Proveedor"
              color={useColorModeValue("secondaryGray.900", "white")}
              fontSize="sm"
            />
          </FormControl>

          {destinationName ? (
            <Flex align="center" fontSize="xs" color="gray.500" gap="4px">
              <Icon as={MdCreateNewFolder} />
              <Text>
                {`Se creará "${destinationName}" › "${trimmedName || "…"}" › "${PO_FACTORY_SUBFOLDER_NAME}"`}
              </Text>
            </Flex>
          ) : (
            <Text fontSize="xs" color="orange.400">
              Selecciona una subcarpeta destino para continuar.
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSaving}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleConfirm}
            isDisabled={!canConfirm}
            isLoading={isSaving}
            loadingText="Guardando"
          >
            Guardar auditoría
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
